import { useState, useMemo } from 'react';
import ProductCard from '@/components/base/ProductCard';
import { recommendedProducts } from '@/mocks/products';
import type { Product } from '@/mocks/products';
import { useDbPrices, mergeDbPrices } from '@/hooks/useDbPrices';
import { useAuth } from '@/hooks/useAuth';

// 카테고리별로 골고루 섞인 추천상품 순서
const recommendedOrder = [
  'atAT01',   // 1 - 아토팜 수딩 젤 로션 (스킨케어·뷰티)
  'p1000',    // 2 - 삼계탕 (탕류)
  'drDR12',   // 3 - 드레스룸 No.20 프루티플로럴 100ml (섬유향수)
  'hbHB03',   // 4 - 바프 아몬드&미니땅콩 세트 (스낵·김)
  'fF11a',    // 5 - 푸른정 완도 마른미역 (해조류)
  'wrWR04',   // 6 - 월드런 베플라이트2 (운동화)
  'iIR01',    // 7 - 황성주 검은콩두유 (음료·두유)
  'drDR18',   // 8 - 드레스룸 No.34 올웨이즈해피 160ml (섬유향수)
  'atAT04',   // 9 - 아토팜 MLE 크림 튜브형 (스킨케어·뷰티)
  'p1004',    // 10 - 소꼬리곰탕 (탕류)
  'hbHB01',   // 11 - 바프 땅콩&아몬드 세트 (스낵·김)
  'fF14a',    // 12 - 푸른정 김밥용김 100장 (해조류)
  'wrWR01',   // 13 - 월드런 슈플라이트3 (운동화)
  'fF18',     // 14 - 가보팜스 꿀유자차 580g (차류)
  'atAT09',   // 15 - 아토팜 키즈 밸런싱 모이스처 젤 로션 (스킨케어·뷰티)
  'hbHB06',   // 16 - 바프 아몬드&믹스넛 8봉 (스낵·김)
  'drDR10',   // 17 - 비건인증 크림 드 퍼퓸 고체향수 (섬유향수)
  'nbNB01',   // 18 - 아이스 넥밴드 (생활용품)
  'giGI01',   // 19 - 건일 발매트 깔창 (건강용품)
  'p1011',    // 19 - 왕갈비탕 (탕류)
  'atAT12',   // 20 - 아토팜 모이스처 바디 워시 (스킨케어·뷰티)
];

const sortedRecommended = [
  ...recommendedOrder
    .map(id => recommendedProducts.find(p => p.id === id))
    .filter((p): p is Product => !!p),
  ...recommendedProducts.filter(p => !recommendedOrder.includes(p.id)),
];

const recommendedIds = sortedRecommended.map(p => p.id);

interface RecommendedSectionProps {
  onInquiry: (product: Product) => void;
}

export default function RecommendedSection({ onInquiry }: RecommendedSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const { role } = useAuth();
  const { dbPrices } = useDbPrices(recommendedIds);

  const mergedProducts = useMemo(
    () => mergeDbPrices(sortedRecommended, dbPrices, role),
    [dbPrices, role]
  );

  const displayed = showAll ? mergedProducts : mergedProducts.slice(0, 8);

  return (
    <section
      id="recommended"
      className="py-16 md:py-20 px-4 md:px-8 lg:px-12"
      style={{ backgroundColor: '#F5F0E8', scrollMarginTop: 'calc(108px + var(--banner-height, 0px))' }}
    >
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: '#C4985A' }}>Recommended</p>
            <h2 className="text-3xl md:text-4xl font-black leading-tight" style={{ color: '#2A2118' }}>本周推荐商品</h2>
            <p className="text-base font-semibold mt-1" style={{ color: '#7A6E64' }}>이번 주 추천 상품</p>
            <p className="text-sm mt-1 leading-relaxed" style={{ color: '#5C4F42' }}>
              食品·化妆品·生活用品 등采购商最常选购의 한국 상품입니다.
              <span className="block text-xs font-medium mt-0.5" style={{ color: '#8C7D6E' }}>식품·화장품·생활용품 등 바이어가 가장 많이 찾는 프리미엄 한국 상품입니다.</span>
            </p>
          </div>
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer self-start sm:self-auto"
            style={{ color: '#C4985A' }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = '#D9B47A')}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = '#C4985A')}
          >
            {showAll ? '收起 / 접기' : '查看全部 / 전체보기'}
            <i className={`${showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-right-s-line'} text-lg`}></i>
          </button>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {displayed.map((product: Product, i: number) => (
            <div
              key={product.id}
              className="animate-fadeIn"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <ProductCard product={product} onInquiry={onInquiry} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
